import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  updateDoc,
  deleteDoc,
  startAfter,
  limit,
  where,
  getDoc,
  increment,
  arrayUnion,
  setDoc
} from 'firebase/firestore';
import { db } from '../../firebase/firestore';
import moment from 'moment';
import Spinner from '../../Components/Spinner';
import { 
  ChevronDown, 
  ChevronUp, 
  Trash2, 
  Edit2, 
  X, 
  Phone, 
  MapPin, 
  Calendar, 
  Clock, 
  Package, 
  CreditCard, 
  User, 
  Mail, 
  Hash,
  Gift,
  AlertCircle,
  Users,
  TrendingUp
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const PAGE_SIZE = 10;

const statusOptions = {
  pending: 'Ожидает',
  paid: 'Оплачен',
  shipped: 'Отправлен',
  done: 'Завершен',
  cancelled: 'Отменен'
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  paid: 'bg-green-100 text-green-800 border-green-200',
  shipped: 'bg-blue-100 text-blue-800 border-blue-200',
  done: 'bg-gray-100 text-gray-800 border-gray-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200'
};

const paymentMethodLabels = {
  cash: 'Наличные курьеру',
  bank_transfer: 'Перевод на карту'
};

const deliveryTypeLabels = {
  mkad: 'Внутри МКАД',
  outside_mkad: 'За МКАД до 10км'
};

const getStatusColor = (status) => statusColors[status] || 'bg-gray-100 text-gray-800 border-gray-200';

// Функция для проверки наличия реферера
const checkReferrerExists = async (userId) => {
  try {
    if (!userId) return { exists: false, referrerId: null };
    
    const userRef = doc(db, 'telegramUsers', userId.toString());
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      console.log('Пользователь не найден при проверке реферера');
      return { exists: false, referrerId: null };
    }
    
    const userData = userSnap.data();
    const referrerId = userData.refereeId || userData.referrerId;
    
    if (!referrerId) {
      console.log('У пользователя нет refereeId/referrerId в данных');
      return { exists: false, referrerId: null };
    }
    
    // Проверяем, существует ли реферер
    const referrerRef = doc(db, 'telegramUsers', referrerId.toString());
    const referrerSnap = await getDoc(referrerRef);
    
    if (!referrerSnap.exists()) {
      console.log('Реферер не найден в базе', { referrerId });
      return { exists: false, referrerId };
    }
    
    console.log('Реферер найден', { referrerId, referrerName: referrerSnap.data().username });
    return { exists: true, referrerId, referrerData: referrerSnap.data() };
    
  } catch (error) {
    console.error('Ошибка при проверке реферера:', error);
    return { exists: false, referrerId: null, error: error.message };
  }
};

// Функция для начисления реферального вознаграждения
const awardReferralBonus = async (orderId, userId, orderAmount) => {
  try {
    console.log('Начисление реферального бонуса:', { orderId, userId, orderAmount });
    
    // Проверяем наличие реферера
    const referrerCheck = await checkReferrerExists(userId);
    
    if (!referrerCheck.exists) {
      console.log('У пользователя нет действующего реферера', { referrerId: referrerCheck.referrerId });
      return { 
        success: false, 
        message: 'У пользователя нет действующего реферера',
        noReferrer: true 
      };
    }
    
    const { referrerId, referrerData } = referrerCheck;
    
    // Проверяем, не начислялся ли уже бонус за этот заказ
    const transactionsRef = collection(db, 'transactions');
    const existingTransactionQuery = query(
      transactionsRef,
      where('orderId', '==', orderId),
      where('type', '==', 'referral_bonus')
    );
    
    const existingTransactionSnap = await getDocs(existingTransactionQuery);
    
    if (!existingTransactionSnap.empty) {
      console.log('Бонус уже начислен за этот заказ');
      return { 
        success: false, 
        message: 'Реферальный бонус уже начислен за этот заказ',
        alreadyAwarded: true 
      };
    }
    
    // Получаем данные заказа для точного расчета
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    let orderData = {};
    
    if (orderSnap.exists()) {
      orderData = orderSnap.data();
    }
    
    // Рассчитываем бонус (10% от суммы товаров, без учета доставки)
    const deliveryCost = orderData.deliveryCost || 
                        (orderData.deliveryAddress?.deliveryCost || 0);
    const orderSubtotal = orderData.subtotal || (orderAmount - deliveryCost);
    const bonusAmount = Math.round(orderSubtotal * 0.1); // 10% от суммы товаров
    
    console.log('Расчет бонуса:', {
      orderAmount,
      deliveryCost,
      orderSubtotal,
      bonusAmount,
      hasDeliveryCost: !!deliveryCost
    });
    
    if (bonusAmount <= 0) {
      console.log('Сумма бонуса меньше или равна 0');
      return { 
        success: false, 
        message: 'Сумма бонуса недействительна',
        invalidAmount: true 
      };
    }
    
    // Обновляем баланс реферера
    const referrerRef = doc(db, 'telegramUsers', referrerId);
    await updateDoc(referrerRef, {
      balance: increment(bonusAmount),
      refBonus: increment(bonusAmount),
      lastActive: new Date(),
      totalEarned: increment(bonusAmount)
    });
    
    // Создаем запись о транзакции - используем setDoc для нового документа
    const transactionRef = doc(collection(db, 'transactions'));
    await setDoc(transactionRef, {
      id: transactionRef.id,
      userId: referrerId,
      referralId: userId,
      orderId: orderId,
      type: 'referral_bonus',
      amount: bonusAmount,
      status: 'completed',
      description: `Реферальный бонус за заказ #${orderId.slice(-8).toUpperCase()}`,
      createdAt: new Date(),
      metadata: {
        orderAmount,
        orderSubtotal,
        deliveryCost,
        bonusPercent: 10,
        referrerName: referrerData.username || referrerData.fullName || 'Пользователь',
        referralName: (await getDoc(doc(db, 'telegramUsers', userId))).data()?.username || 'Пользователь'
      }
    });
    
    // Обновляем информацию о реферале в данных реферера
    const userSnap = await getDoc(doc(db, 'telegramUsers', userId));
    const userData = userSnap.exists() ? userSnap.data() : {};
    
    const referralInfo = {
      userId: userId,
      username: userData.username || 'Пользователь',
      orderId: orderId,
      orderAmount: orderAmount,
      orderSubtotal: orderSubtotal,
      bonusAmount: bonusAmount,
      awardedAt: new Date()
    };
    
    // Добавляем или обновляем запись о реферале
    const referralsArray = referrerData.referrals || [];
    const existingReferralIndex = referralsArray.findIndex(r => r.userId === userId);
    
    if (existingReferralIndex >= 0) {
      // Обновляем существующего реферала
      referralsArray[existingReferralIndex] = {
        ...referralsArray[existingReferralIndex],
        ...referralInfo,
        totalEarned: (referralsArray[existingReferralIndex].totalEarned || 0) + bonusAmount,
        lastOrder: new Date(),
        ordersCount: (referralsArray[existingReferralIndex].ordersCount || 0) + 1
      };
    } else {
      // Добавляем нового реферала
      referralsArray.push({
        ...referralInfo,
        joinedAt: userData.createdAt || new Date(),
        totalEarned: bonusAmount,
        lastOrder: new Date(),
        ordersCount: 1
      });
    }
    
    await updateDoc(referrerRef, {
      referrals: referralsArray
    });
    
    // Отмечаем в заказе, что бонус начислен
    await updateDoc(orderRef, {
      referralBonusAwarded: true,
      referrerId: referrerId,
      referralBonusAmount: bonusAmount
    });
    
    console.log('Реферальный бонус успешно начислен:', {
      referrerId,
      bonusAmount,
      transactionId: transactionRef.id
    });
    
    return { 
      success: true, 
      message: `Начислено ${bonusAmount} ₽ рефералу ${referrerId}`,
      bonusAmount,
      referrerId,
      referrerName: referrerData.username || referrerData.fullName || 'Пользователь'
    };
    
  } catch (error) {
    console.error('Ошибка при начислении реферального бонуса:', error);
    return { 
      success: false, 
      message: `Ошибка: ${error.message}`,
      error: true 
    };
  }
};

const AdminOrdersPanel = () => {
  const [orders, setOrders] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState([]);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('dateDesc');
  const [editOrder, setEditOrder] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editStreet, setEditStreet] = useState('');
  const [editDeliveryInstructions, setEditDeliveryInstructions] = useState('');
  const [editDeliveryDate, setEditDeliveryDate] = useState('');
  const [editDeliveryTime, setEditDeliveryTime] = useState('');
  const [editDeliveryType, setEditDeliveryType] = useState('');
  const [editPaymentMethod, setEditPaymentMethod] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [bonusNotification, setBonusNotification] = useState(null);
  const [referrerChecks, setReferrerChecks] = useState({});

  // Функция для проверки реферера для конкретного заказа
  const checkOrderReferrer = async (userId, orderId) => {
    if (!userId) return { hasReferrer: false };
    
    // Используем кэш если уже проверяли
    if (referrerChecks[orderId]) {
      return referrerChecks[orderId];
    }
    
    const result = await checkReferrerExists(userId);
    const checkResult = { 
      hasReferrer: result.exists, 
      referrerId: result.referrerId,
      referrerData: result.referrerData 
    };
    
    // Сохраняем в кэш
    setReferrerChecks(prev => ({
      ...prev,
      [orderId]: checkResult
    }));
    
    return checkResult;
  };

  const updateOrderStatus = async (orderId, userId, newStatus, orderAmount) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      
      // Если статус меняется на "оплачен", начисляем реферальный бонус
      if (newStatus === 'paid') {
        // Проверяем наличие реферера
        const referrerCheck = await checkOrderReferrer(userId, orderId);
        
        if (!referrerCheck.hasReferrer) {
          // Просто обновляем статус без начисления бонуса
          await updateDoc(orderRef, { status: newStatus });
          setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, status: newStatus } : o)));
          
          setBonusNotification({
            type: 'info',
            message: `✅ Статус изменен на "Оплачен". У пользователя нет реферера для начисления бонуса.`
          });
          
          setTimeout(() => setBonusNotification(null), 5000);
          return;
        }
        
        const orderDoc = await getDoc(orderRef);
        const orderData = orderDoc.data();
        
        // Проверяем, был ли заказ уже оплачен ранее
        if (orderData.status !== 'paid') {
          const result = await awardReferralBonus(orderId, userId, orderAmount);
          
          if (result.success) {
            setBonusNotification({
              type: 'success',
              message: `✅ ${result.message}`,
              bonusAmount: result.bonusAmount,
              referrerId: result.referrerId,
              referrerName: result.referrerName
            });
            
            setTimeout(() => setBonusNotification(null), 5000);
          } else if (result.noReferrer) {
            // Просто обновляем статус
            setBonusNotification({
              type: 'info',
              message: `✅ Статус изменен на "Оплачен". У пользователя нет реферера.`
            });
            setTimeout(() => setBonusNotification(null), 5000);
          } else if (result.alreadyAwarded) {
            setBonusNotification({
              type: 'warning',
              message: `⚠️ Бонус уже начислен ранее. Статус изменен на "Оплачен".`
            });
            setTimeout(() => setBonusNotification(null), 5000);
          } else {
            setBonusNotification({
              type: 'warning',
              message: `⚠️ ${result.message} Статус изменен на "Оплачен".`
            });
            setTimeout(() => setBonusNotification(null), 5000);
          }
        }
      }
      
      // Обновляем статус заказа
      await updateDoc(orderRef, { status: newStatus });
      setOrders(prev => prev.map(o => (o.id === orderId ? { ...o, status: newStatus } : o)));
      
    } catch (err) {
      console.error('Ошибка при обновлении статуса заказа:', err);
      setBonusNotification({
        type: 'error',
        message: `❌ Ошибка при обновлении статуса: ${err.message}`
      });
    }
  };

  const fetchOrders = async (isInitial = false) => {
    try {
      const baseQuery = query(
        collection(db, 'orders'),
        orderBy('createdAt', 'desc'),
        ...(lastDoc && !isInitial ? [startAfter(lastDoc)] : []),
        limit(PAGE_SIZE)
      );
      const snapshot = await getDocs(baseQuery);
      const data = await Promise.all(snapshot.docs.map(async (docSnap) => {
        const d = docSnap.data();
        
        // Проверяем наличие реферера для каждого заказа
        let hasReferrer = false;
        let referrerId = null;
        if (d.userId) {
          const referrerCheck = await checkOrderReferrer(d.userId, docSnap.id);
          hasReferrer = referrerCheck.hasReferrer;
          referrerId = referrerCheck.referrerId;
        }
        
        return {
          id: docSnap.id,
          userId: d.userId || null,
          fullName: d.fullName || '',
          username: d.username || '',
          email: d.email || '',
          phone: d.phone || '',
          status: d.status || 'pending',
          deliveryAddress: d.deliveryAddress || {
            street: '',
            deliveryInstructions: '',
            deliveryDate: '',
            deliveryTime: '',
            deliveryType: '',
            deliveryCost: 0
          },
          paymentMethod: d.paymentMethod || '',
          items: d.items || [],
          subtotal: d.subtotal || 0,
          deliveryCost: d.deliveryCost || 0,
          total: d.total || 0,
          referralBonusAwarded: d.referralBonusAwarded || false,
          referrerId: d.referrerId || referrerId,
          referralBonusAmount: d.referralBonusAmount || 0,
          hasReferrer: hasReferrer,
          createdAt: d.createdAt?.toDate ? d.createdAt.toDate() : new Date()
        };
      }));
      
      setOrders(prev => (isInitial ? data : [...prev, ...data]));
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
    } catch (error) {
      console.error('Ошибка загрузки заказов:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchOrders(true);
  }, []);

  const toggleExpand = (orderId) => {
    setExpanded(prev => prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Удалить этот заказ?')) return;
    try {
      await deleteDoc(doc(db, 'orders', orderId));
      setOrders(prev => prev.filter(order => order.id !== orderId));
    } catch (err) {
      console.error('Ошибка при удалении заказа:', err);
    }
  };

  const openEditModal = (order) => {
    setEditOrder(order);
    setEditStatus(order.status || 'pending');
    setEditStreet(order.deliveryAddress?.street || '');
    setEditDeliveryInstructions(order.deliveryAddress?.deliveryInstructions || '');
    setEditDeliveryDate(order.deliveryAddress?.deliveryDate || '');
    setEditDeliveryTime(order.deliveryAddress?.deliveryTime || '');
    setEditDeliveryType(order.deliveryAddress?.deliveryType || '');
    setEditPaymentMethod(order.paymentMethod || '');
  };

  const saveEdit = async () => {
    if (!editOrder) return;
    setSaving(true);
    try {
      // Сохраняем изменения в заказе
      const orderRef = doc(db, 'orders', editOrder.id);
      
      // Сначала обновляем статус с начислением бонуса если нужно
      await updateOrderStatus(editOrder.id, editOrder.userId, editStatus, editOrder.total);
      
      // Затем обновляем остальные поля
      await updateDoc(orderRef, {
        deliveryAddress: {
          street: editStreet,
          deliveryInstructions: editDeliveryInstructions,
          deliveryDate: editDeliveryDate,
          deliveryTime: editDeliveryTime,
          deliveryType: editDeliveryType,
          deliveryCost: editDeliveryType === 'mkad' ? 800 : editDeliveryType === 'outside_mkad' ? 1300 : 0
        },
        paymentMethod: editPaymentMethod
      });

      // Обновляем локальное состояние
      setOrders(prev =>
        prev.map(o =>
          o.id === editOrder.id
            ? {
                ...o,
                status: editStatus,
                deliveryAddress: {
                  ...o.deliveryAddress,
                  street: editStreet,
                  deliveryInstructions: editDeliveryInstructions,
                  deliveryDate: editDeliveryDate,
                  deliveryTime: editDeliveryTime,
                  deliveryType: editDeliveryType,
                  deliveryCost: editDeliveryType === 'mkad' ? 800 : editDeliveryType === 'outside_mkad' ? 1300 : 0
                },
                paymentMethod: editPaymentMethod,
                total: o.subtotal + (editDeliveryType === 'mkad' ? 800 : editDeliveryType === 'outside_mkad' ? 1300 : 0)
              }
            : o
        )
      );
    } catch (err) {
      console.error('Ошибка при сохранении редактирования:', err);
      setBonusNotification({
        type: 'error',
        message: `❌ Ошибка сохранения: ${err.message}`
      });
    } finally {
      setSaving(false);
      setEditOrder(null);
    }
  };

  // Быстрая смена статуса без модального окна
  const quickUpdateStatus = async (orderId, userId, newStatus, orderAmount, hasReferrer) => {
    if (newStatus === 'paid') {
      let message = 'Изменить статус на "Оплачен"?';
      
      if (hasReferrer) {
        // Получаем актуальные данные заказа для расчета
        const orderRef = doc(db, 'orders', orderId);
        const orderSnap = await getDoc(orderRef);
        const orderData = orderSnap.exists() ? orderSnap.data() : {};
        
        const deliveryCost = orderData.deliveryCost || 0;
        const orderSubtotal = orderData.subtotal || (orderAmount - deliveryCost);
        const bonusAmount = Math.round(orderSubtotal * 0.1);
        
        message += `\n\nПри этом будет начислено реферальное вознаграждение: ${bonusAmount} ₽ (10% от суммы товаров)`;
      } else {
        message += '\n\nУ пользователя нет реферера. Реферальное вознаграждение не будет начислено.';
      }
      
      if (!window.confirm(message)) {
        return;
      }
    }
    
    try {
      await updateOrderStatus(orderId, userId, newStatus, orderAmount);
    } catch (error) {
      console.error('Ошибка при быстром обновлении статуса:', error);
    }
  };

  const filtered = orders
    .filter(order =>
      (!filter || order.status === filter) &&
      (order.fullName.toLowerCase().includes(search.toLowerCase()) ||
       order.username.toLowerCase().includes(search.toLowerCase()) ||
       order.phone.includes(search))
    )
    .sort((a, b) => {
      if (sort === 'dateDesc') return b.createdAt - a.createdAt;
      if (sort === 'dateAsc') return a.createdAt - b.createdAt;
      if (sort === 'totalAsc') return a.total - b.total;
      if (sort === 'totalDesc') return b.total - a.total;
      return 0;
    });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Определяем цвет рамки в зависимости от наличия реферера
  const getOrderBorderColor = (order) => {
    if (!order.hasReferrer) return 'border-gray-200';
    
    if (order.status === 'paid') {
      return 'border-green-300 border-2';
    } else if (order.status === 'pending') {
      return 'border-orange-300 border-2';
    } else {
      return 'border-blue-300 border-2';
    }
  };

  return (
    <div className="w-full min-h-screen p-4 pt-12 text-gray-900 relative bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Уведомление о начислении бонуса */}
      <AnimatePresence>
        {bonusNotification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 rounded-xl shadow-lg p-4 max-w-md w-full ${
              bonusNotification.type === 'success' 
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-800' 
                : bonusNotification.type === 'warning'
                ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 text-yellow-800'
                : bonusNotification.type === 'error'
                ? 'bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 text-red-800'
                : 'bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 text-blue-800'
            }`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-3">
                {bonusNotification.type === 'success' ? (
                  <Gift className="h-5 w-5 text-green-500" />
                ) : bonusNotification.type === 'warning' ? (
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                ) : bonusNotification.type === 'error' ? (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-blue-500" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium">{bonusNotification.message}</p>
                {bonusNotification.bonusAmount && (
                  <p className="text-sm mt-1 opacity-90">
                    Сумма бонуса: <span className="font-bold">{formatCurrency(bonusNotification.bonusAmount)} ₽</span>
                  </p>
                )}
                {bonusNotification.referrerId && (
                  <p className="text-xs mt-1 opacity-80">
                    ID реферера: <code className="bg-white/50 px-2 py-1 rounded">{bonusNotification.referrerId}</code>
                    {bonusNotification.referrerName && (
                      <span className="ml-2">({bonusNotification.referrerName})</span>
                    )}
                  </p>
                )}
              </div>
              <button
                onClick={() => setBonusNotification(null)}
                className="ml-3 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="sticky top-0 pb-2 z-10 bg-gradient-to-b from-gray-50 via-gray-50 to-transparent pt-2">
        <h1 className="text-2xl font-bold mb-4 text-gray-800 font-Syne">Управление заказами</h1>
        
        {/* Информация о реферальной программе */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-3 mb-4">
          <div className="flex items-center">
            <TrendingUp className="h-5 w-5 text-orange-500 mr-2" />
            <div>
              <p className="text-sm font-medium text-orange-800">
                Реферальная программа
              </p>
              <p className="text-xs text-orange-600">
                Заказы с реферерами выделены цветной рамкой. При оплате начисляется 10% реферального бонуса.
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-md p-4 mb-4 border border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <Hash size={18} />
                </div>
                <input
                  type="text"
                  placeholder="Поиск по имени, @username или телефону"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="">Все статусы</option>
                {Object.entries(statusOptions).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="dateDesc">Сначала новые</option>
                <option value="dateAsc">Сначала старые</option>
                <option value="totalDesc">По сумме ↓</option>
                <option value="totalAsc">По сумме ↑</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package size={24} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Заказов не найдено</h3>
          <p className="text-gray-500">Попробуйте изменить фильтры поиска</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-1 pb-32">
          {filtered.map(order => {
            const isOpen = expanded.includes(order.id);
            const addr = order.deliveryAddress;
            const deliveryCost = addr?.deliveryCost || 0;
            const subtotal = order.subtotal || order.total - deliveryCost;
            const potentialBonus = order.hasReferrer ? Math.round(subtotal * 0.1) : 0;
            
            return (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`relative bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300 ${getOrderBorderColor(order)}`}
              >
                {/* Индикатор реферера */}
                {order.hasReferrer && (
                  <div className="absolute top-0 left-0 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-3 py-1 rounded-br-lg text-xs font-medium border-b border-r flex items-center">
                    <Users size={12} className="mr-1" />
                    <span>Реферал</span>
                  </div>
                )}
                
{/* Status badge с быстрыми действиями */}
<div className={`absolute top-0 right-0 ${getStatusColor(order.status)} px-3 py-1 rounded-bl-lg text-xs font-medium border-b border-l flex items-center z-10`}>
  <span className="mr-1">{statusOptions[order.status] || order.status}</span>
  
  {/* Кнопки быстрой смены статуса */}
  {order.status !== 'paid' && order.status !== 'cancelled' && (
    <button
      onClick={(e) => {
        e.stopPropagation();
        quickUpdateStatus(order.id, order.userId, 'paid', order.total, order.hasReferrer);
      }}
      title="Отметить как оплачен"
      className={`w-5 h-5 rounded text-xs flex items-center justify-center hover:opacity-90 transition-colors ${
        order.hasReferrer 
          ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' 
          : 'bg-gray-300 text-gray-600'
      }`}
    >
      $
    </button>
  )}
</div>
                
                {/* Order header */}
                <div
                  onClick={() => toggleExpand(order.id)}
                  className="cursor-pointer transition-all duration-300 p-8"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <User size={16} className="text-gray-400" />
                        <h2 className="text-lg font-semibold text-gray-900 truncate">
                          {order.fullName}
                          {order.username && <span className="text-gray-500 ml-2">@{order.username}</span>}
                        </h2>
                        {order.hasReferrer && (
                          <span className="text-xs bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 px-2 py-0.5 rounded-full">
                            Реферал
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          <span>{moment(order.createdAt).format('DD.MM.YYYY')}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          <span>{moment(order.createdAt).format('HH:mm')}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Phone size={14} />
                          <span>{order.phone || '—'}</span>
                        </div>
                      </div>
                      
                      {order.email && (
                        <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                          <Mail size={14} />
                          <span>{order.email}</span>
                        </div>
                      )}
                      
                      {order.hasReferrer && order.referrerId && (
                        <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                          <Users size={14} />
                          <span className="text-xs">
                            Реферер: <code className="bg-gray-100 px-1 py-0.5 rounded">{order.referrerId}</code>
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="text-right ml-4">
                      <div className="text-2xl font-bold text-blue-600 mb-1">
                        {formatCurrency(order.total)} ₽
                      </div>
                      <div className="text-sm text-gray-500">
                        {order.items?.length || 0} товар{order.items?.length === 1 ? '' : order.items?.length > 1 && order.items?.length < 5 ? 'а' : 'ов'}
                      </div>
                      
                      {/* Иконка реферального бонуса - ПЕРЕМЕЩЕНА И УМЕНЬШЕНА */}
                      {order.status === 'paid' && order.hasReferrer && (
                        <div className="flex items-center justify-end mt-2">
                          <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 backdrop-blur-sm border border-green-200/50 text-green-700 text-xs px-2 py-1 rounded-lg flex items-center opacity-80">
                            <Gift size={10} className="mr-1" />
                            <span>{formatCurrency(order.referralBonusAmount || potentialBonus)} ₽</span>
                            <span className="text-[10px] ml-1 opacity-70">реферальный бонус</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-center mt-3">
                    <ChevronDown
                      size={20}
                      className={`text-gray-400 transition-transform duration-300 ${isOpen ? 'transform rotate-180' : ''}`}
                    />
                  </div>
                </div>

                {/* Expanded details */}
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-5 pb-5 space-y-4 border-t border-gray-100"
                    >
                      {/* Реферальная информация - ТОЛЬКО ЕСЛИ ЕСТЬ РЕФЕРЕР */}
                      {order.hasReferrer && (
                        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <TrendingUp className="h-5 w-5 text-orange-500 mr-2" />
                              <div>
                                <h4 className="font-medium text-orange-800">Реферальная программа</h4>
                                <p className="text-sm text-orange-600">
                                  Этот заказ пришел от реферала. При оплате начисляется {formatCurrency(potentialBonus)} ₽ рефералу (10% от суммы товаров)
                                </p>
                              </div>
                            </div>
                            
                            {order.status === 'paid' ? (
                              <div className="flex items-center bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1.5 rounded-full text-sm font-medium">
                                <Gift size={14} className="mr-1" />
                                Бонус начислен
                              </div>
                            ) : (
                              <button
                                onClick={() => quickUpdateStatus(order.id, order.userId, 'paid', order.total, true)}
                                className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-2 rounded-lg font-medium hover:shadow-md transition-all active:scale-95"
                              >
                                Начислить бонус
                              </button>
                            )}
                          </div>
                          
                          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                            <div className="text-gray-600">
                              ID пользователя:
                            </div>
                            <div className="font-mono text-xs font-medium bg-white px-2 py-1 rounded border border-orange-200">
                              {order.userId || '—'}
                            </div>
                            
                            <div className="text-gray-600">
                              ID реферера:
                            </div>
                            <div className="font-mono text-xs font-medium bg-white px-2 py-1 rounded border border-orange-200">
                              {order.referrerId || referrerChecks[order.id]?.referrerId || '—'}
                            </div>
                            
                            <div className="text-gray-600">
                              Потенциальный бонус:
                            </div>
                            <div className="font-bold text-green-600">
                              {formatCurrency(potentialBonus)} ₽
                            </div>
                            
                            {order.referralBonusAwarded && (
                              <>
                                <div className="text-gray-600">
                                  Фактический бонус:
                                </div>
                                <div className="font-bold text-green-600">
                                  {formatCurrency(order.referralBonusAmount || potentialBonus)} ₽
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Order details grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Delivery info */}
                        <div className="space-y-3">
                          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                            <MapPin size={18} className="text-blue-500" />
                            Информация о доставке
                          </h3>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex items-start gap-2">
                              <span className="text-gray-500 min-w-[120px]">Адрес:</span>
                              <span className="font-medium">{addr?.street || '—'}</span>
                            </div>
                            
                            {addr?.deliveryInstructions && (
                              <div className="flex items-start gap-2">
                                <span className="text-gray-500 min-w-[120px]">Как пройти:</span>
                                <span className="font-medium">{addr.deliveryInstructions}</span>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 min-w-[120px]">Дата:</span>
                              <span className="font-medium">{addr?.deliveryDate || '—'}</span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 min-w-[120px]">Время:</span>
                              <span className="font-medium">{addr?.deliveryTime || '—'}</span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 min-w-[120px]">Тип доставки:</span>
                              <span className="font-medium">
                                {addr?.deliveryType ? deliveryTypeLabels[addr.deliveryType] : '—'}
                                {addr?.deliveryCost ? ` (${formatCurrency(addr.deliveryCost)} ₽)` : ''}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Payment & user info */}
                        <div className="space-y-3">
                          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                            <CreditCard size={18} className="text-green-500" />
                            Оплата и данные
                          </h3>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 min-w-[120px]">Способ оплаты:</span>
                              <span className="font-medium">
                                {order.paymentMethod ? paymentMethodLabels[order.paymentMethod] || order.paymentMethod : '—'}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 min-w-[120px]">ID пользователя:</span>
                              <span className="font-mono text-xs font-medium bg-gray-100 px-2 py-1 rounded">
                                {order.userId || '—'}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500 min-w-[120px]">Номер заказа:</span>
                              <span className="font-mono text-xs font-medium bg-gray-100 px-2 py-1 rounded">
                                #{order.id.slice(-8).toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Order items */}
                      <div>
                        <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                          <Package size={18} className="text-purple-500" />
                          Состав заказа
                        </h3>
                        
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="space-y-2 max-h-40 overflow-y-auto pr-2">
                            {order.items?.map((item, i) => (
                              <div key={i} className="flex justify-between items-center py-1.5 border-b border-gray-200 last:border-0">
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium truncate">{item.title}</div>
                                  <div className="text-xs text-gray-500">
                                    Артикул: {item.id}
                                  </div>
                                </div>
                                <div className="text-right ml-4 whitespace-nowrap">
                                  <div className="font-semibold">
                                    {formatCurrency(item.price)} ₽ × {item.quantity} = {formatCurrency(item.price * item.quantity)} ₽
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Totals */}
                          <div className="mt-3 pt-3 border-t border-gray-300 space-y-1">
                            <div className="flex justify-between text-sm">
                              <span>Сумма товаров:</span>
                              <span className="font-semibold">{formatCurrency(subtotal)} ₽</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span>Доставка:</span>
                              <span className="font-semibold">{formatCurrency(deliveryCost)} ₽</span>
                            </div>
                            <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300">
                              <span>Итого к оплате:</span>
                              <span className="text-blue-600">{formatCurrency(order.total)} ₽</span>
                            </div>
                            
                            {/* Реферальный расчет - ТОЛЬКО ЕСЛИ ЕСТЬ РЕФЕРЕР */}
                            {order.hasReferrer && (
                              <div className="mt-2 pt-2 border-t border-dashed border-gray-300">
                                <div className="flex justify-between text-sm text-green-600">
                                  <span>Реферальный бонус (10%):</span>
                                  <span className="font-bold">+{formatCurrency(potentialBonus)} ₽</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action buttons - ДОБАВЛЕН КОНТЕЙНЕР С FIXED POSITION */}
                      <div className="sticky bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 mt-4 -mx-5 -mb-5 px-5 pb-12 py-4 z-10">
                        <div className="flex justify-between items-center">
                          <div className="flex gap-3">
                            <button
                              onClick={() => openEditModal(order)}
                              className="flex items-center gap-2 px-8 py-4 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium"
                            >
                              <Edit2 size={16} />
                              Редактировать
                            </button>
                            <button
                              onClick={() => handleDelete(order.id)}
                              className="flex items-center gap-2 px-8 py-4 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium"
                            >
                              <Trash2 size={16} />
                              Удалить
                            </button>
                          </div>
                          
                          {/* Быстрая кнопка оплаты - С ИНДИКАТОРОМ РЕФЕРЕРА */}
                          {order.status !== 'paid' && order.status !== 'cancelled' && (
                            <button
                              onClick={() => quickUpdateStatus(order.id, order.userId, 'paid', order.total, order.hasReferrer)}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg hover:shadow-md transition-all active:scale-95 font-medium ${
                                order.hasReferrer
                                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              <Gift size={16} />
                              {order.hasReferrer ? 'Отметить оплаченным' : 'Без реферера'}
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}

          {/* Load more button */}
          {lastDoc && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => { setLoadingMore(true); fetchOrders(false); }}
                disabled={loadingMore}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? 'Загрузка...' : 'Загрузить еще'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Edit modal */}
      {editOrder && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
          onClick={() => setEditOrder(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white p-6 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full"
              onClick={() => setEditOrder(null)}
            >
              <X size={24} />
            </button>
            
            <h2 className="text-2xl font-bold mb-6 text-gray-800 font-Syne">Редактирование заказа</h2>
            
            {/* Информация о реферальном бонусе в модальном окне - ТОЛЬКО ЕСЛИ ЕСТЬ РЕФЕРЕР */}
            {editStatus === 'paid' && editOrder.hasReferrer && (
              <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-start">
                  <Gift className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-green-800 mb-1">Реферальный бонус</h3>
                    <p className="text-sm text-green-600 mb-2">
                      При сохранении статуса "Оплачен" будет автоматически начислен реферальный бонус в размере 10% от суммы товаров.
                    </p>
                    <div className="text-sm font-medium text-green-700">
                      Примерный бонус: <span className="font-bold">{formatCurrency(Math.round((editOrder.total - (editDeliveryType === 'mkad' ? 800 : editDeliveryType === 'outside_mkad' ? 1300 : 0)) * 0.1))} ₽</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {editStatus === 'paid' && !editOrder.hasReferrer && (
              <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-800 mb-1">Нет реферера</h3>
                    <p className="text-sm text-blue-600">
                      У пользователя нет реферера. Реферальный бонус не будет начислен при изменении статуса на "Оплачен".
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Статус заказа
                  </label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {Object.entries(statusOptions).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Тип доставки
                  </label>
                  <select
                    value={editDeliveryType}
                    onChange={(e) => setEditDeliveryType(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Выберите тип</option>
                    <option value="mkad">Внутри МКАД (800 ₽)</option>
                    <option value="outside_mkad">За МКАД до 10км (1 300 ₽)</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Способ оплаты
                  </label>
                  <select
                    value={editPaymentMethod}
                    onChange={(e) => setEditPaymentMethod(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Выберите способ</option>
                    <option value="cash">Наличные курьеру</option>
                    <option value="bank_transfer">Перевод на карту</option>
                  </select>
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Адрес доставки
                  </label>
                  <input
                    type="text"
                    value={editStreet}
                    onChange={(e) => setEditStreet(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Улица, дом, квартира"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Как пройти (для курьера)
                  </label>
                  <textarea
                    value={editDeliveryInstructions}
                    onChange={(e) => setEditDeliveryInstructions(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[80px]"
                    placeholder="Код домофона, подъезд, этаж"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Дата доставки
                    </label>
                    <input
                      type="date"
                      value={editDeliveryDate}
                      onChange={(e) => setEditDeliveryDate(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Время доставки
                    </label>
                    <input
                      type="time"
                      value={editDeliveryTime}
                      onChange={(e) => setEditDeliveryTime(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => setEditOrder(null)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Отмена
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Сохранение...' : 'Сохранить изменения'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* УВЕЛИЧЕННОЕ пустое пространство внизу для скролла */}
      <div className="h-32"></div>
    </div>
  );
};

export default AdminOrdersPanel;